/**
 * Iter 216 — Panneau de réglages avancés Sécurité.
 * Affiche dans l'onglet Sécurité de Settings :
 *  - Switch auto-block + seuils
 *  - Allowlist / Blocklist d'IPs
 *  - Liste des IPs actuellement bloquées avec bouton unblock
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, Save, Shield, Ban, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

export default function SecuritySettingsPanel() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [settings, setSettings] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBlockIp, setNewBlockIp] = useState("");
  const [newAllowIp, setNewAllowIp] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/security/settings"),
        api.get("/security/ip-blocks"),
      ]);
      setSettings(s.data);
      setBlocks(b.data?.blocks || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/security/settings", settings);
    } finally {
      setSaving(false);
    }
  };

  const addBlocklist = async () => {
    const ip = newBlockIp.trim();
    if (!ip) return;
    await api.post("/security/ip-block", { ip, reason: "manual", duration_hours: settings?.auto_block_duration_hours || 24 });
    setNewBlockIp("");
    reload();
  };

  const addAllowlist = () => {
    const ip = newAllowIp.trim();
    if (!ip || (settings.ip_allowlist || []).includes(ip)) return;
    setSettings({ ...settings, ip_allowlist: [...(settings.ip_allowlist || []), ip] });
    setNewAllowIp("");
  };

  const removeAllowlist = (ip) => {
    setSettings({ ...settings, ip_allowlist: (settings.ip_allowlist || []).filter((x) => x !== ip) });
  };

  const unblock = async (ip) => {
    await api.delete(`/security/ip-block/${ip}`);
    reload();
  };

  if (loading || !settings) {
    return <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4 text-xs text-slate-400 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Chargement réglages…</div>;
  }

  return (
    <div className="space-y-4" data-testid="security-settings-panel">
      {/* Auto-block */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <h4 className="text-sm font-bold text-white inline-flex items-center gap-2">
              <Ban className="w-4 h-4 text-rose-400" /> Blocage automatique des IPs
            </h4>
            <p className="text-[11px] text-slate-500 mt-0.5">Bloque automatiquement les IPs ayant trop d'alertes critiques en peu de temps.</p>
          </div>
          <Switch checked={!!settings.auto_block_enabled} onCheckedChange={(v) => setSettings({ ...settings, auto_block_enabled: v })} data-testid="auto-block-toggle" />
        </div>
        {settings.auto_block_enabled && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t border-white/5">
            <NumField label="Seuil critique" value={settings.auto_block_threshold_critical} onChange={(v) => setSettings({ ...settings, auto_block_threshold_critical: v })} suffix="alertes" testid="threshold-critical" />
            <NumField label="Fenêtre" value={settings.auto_block_window_minutes} onChange={(v) => setSettings({ ...settings, auto_block_window_minutes: v })} suffix="min" testid="window-minutes" />
            <NumField label="Durée du blocage" value={settings.auto_block_duration_hours} onChange={(v) => setSettings({ ...settings, auto_block_duration_hours: v })} suffix="h" testid="duration-hours" />
          </div>
        )}
      </div>

      {/* Allowlist */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
        <h4 className="text-sm font-bold text-white inline-flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Allowlist (IPs jamais bloquées)
        </h4>
        <div className="flex items-center gap-2 mb-3">
          <Input value={newAllowIp} onChange={(e) => setNewAllowIp(e.target.value)} placeholder="Ex: 89.116.42.0 ou 192.168.1.0/24" className="bg-[#0b0811] border-white/10 text-white text-xs h-8 font-mono" data-testid="allowlist-input" />
          <Button onClick={addAllowlist} variant="outline" size="sm" className="text-xs h-8" data-testid="allowlist-add">
            <Plus className="w-3 h-3 mr-1" /> Ajouter
          </Button>
        </div>
        {settings.ip_allowlist?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {settings.ip_allowlist.map((ip) => (
              <span key={ip} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-300 font-mono" data-testid={`allowlist-${ip}`}>
                {ip}
                <button onClick={() => removeAllowlist(ip)} className="text-emerald-400 hover:text-emerald-200 ml-1">×</button>
              </span>
            ))}
          </div>
        ) : <p className="text-[11px] text-slate-500 italic">Aucune IP allowlistée</p>}
      </div>

      {/* Blocklist actif */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-4" data-testid="blocklist-section">
        <h4 className="text-sm font-bold text-white inline-flex items-center gap-2 mb-3">
          <Ban className="w-4 h-4 text-rose-400" /> IPs bloquées ({blocks.filter(b => !b.expired).length} actives)
        </h4>
        <div className="flex items-center gap-2 mb-3">
          <Input value={newBlockIp} onChange={(e) => setNewBlockIp(e.target.value)} placeholder="Bloquer une IP manuellement" className="bg-[#0b0811] border-white/10 text-white text-xs h-8 font-mono" data-testid="blocklist-input" />
          <Button onClick={addBlocklist} variant="outline" size="sm" className="text-xs h-8 border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid="blocklist-add">
            <Ban className="w-3 h-3 mr-1" /> Bloquer
          </Button>
        </div>
        {blocks.length === 0 ? (
          <p className="text-[11px] text-slate-500 italic">Aucune IP bloquée</p>
        ) : (
          <div className="space-y-1">
            {blocks.map((b) => (
              <div key={b.ip} className={`flex items-center justify-between gap-2 text-xs p-2 rounded ${b.expired ? "bg-slate-500/5" : "bg-rose-500/10"}`} data-testid={`block-${b.ip}`}>
                <div>
                  <span className="font-mono text-white">{b.ip}</span>
                  <span className={`ml-2 text-[10px] ${b.expired ? "text-slate-500" : "text-rose-300"}`}>
                    {b.expired ? "expiré" : "actif"} · {b.reason || "manuel"}
                  </span>
                  {b.expires_at && <span className="ml-2 text-[10px] text-slate-500">jusqu'au {new Date(b.expires_at).toLocaleString("fr-FR")}</span>}
                </div>
                <button onClick={() => unblock(b.ip)} className="text-rose-400 hover:text-rose-200 p-1" data-testid={`unblock-${b.ip}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save */}
      <Button onClick={save} disabled={saving} className="bg-[#f7c935] hover:bg-[#e6b820] text-[#14101c] text-xs h-8" data-testid="save-security-settings">
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
        Enregistrer les réglages
      </Button>
    </div>
  );
}

function NumField({ label, value, onChange, suffix, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase text-slate-500 font-bold tracking-wider mb-1 block">{label}</label>
      <div className="flex items-center gap-1">
        <Input type="number" value={value || 0} onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)} className="bg-[#0b0811] border-white/10 text-white text-xs h-8 w-20" data-testid={testid} />
        <span className="text-[10px] text-slate-500">{suffix}</span>
      </div>
    </div>
  );
}
